/**
 * Läden und Einkaufstage auf dem Gerät (18.10.2026). Läden gehören dem
 * Haushalt wie die Liste; ein Einkaufstag ist ein Haushaltstermin mit
 * `shopping` und optional einem Laden — er steht deshalb auch im Kalender.
 */

import { moveShelf, newId } from '@kraftwerk/shared';
import type { ShelfCategory } from '@kraftwerk/shared';
import { db } from './local.js';
import type { KraftwerkDatabase, LocalHouseholdEvent, LocalShop } from './local.js';
import { stamp, writeRow } from './write.js';

export async function listShops(
  householdId: string | null,
  database: KraftwerkDatabase = db,
): Promise<LocalShop[]> {
  if (!householdId) return [];
  return (await database.shops.where('householdId').equals(householdId).toArray())
    .filter((row) => !row.deletedAt)
    .sort((a, b) => a.position - b.position || a.name.localeCompare(b.name, 'de'));
}

export async function addShop(
  householdId: string,
  name: string,
  database: KraftwerkDatabase = db,
): Promise<LocalShop> {
  const position = (await listShops(householdId, database)).length;
  return writeRow(
    'shops',
    {
      id: newId(),
      updatedAt: stamp(),
      deletedAt: null,
      householdId,
      name: name.trim(),
      shelfOrder: null,
      position,
    },
    'upsert',
    database,
  );
}

async function changeShop(
  id: string,
  change: (shop: LocalShop) => Partial<LocalShop>,
  database: KraftwerkDatabase,
): Promise<void> {
  const shop = await database.shops.get(id);
  if (!shop || shop.deletedAt) return;
  await writeRow('shops', { ...shop, ...change(shop), updatedAt: stamp() }, 'upsert', database);
}

export function renameShop(id: string, name: string, database: KraftwerkDatabase = db) {
  return changeShop(id, () => ({ name: name.trim() }), database);
}

export function moveShopShelf(
  id: string,
  shelf: ShelfCategory,
  step: -1 | 1,
  database: KraftwerkDatabase = db,
) {
  return changeShop(
    id,
    (shop) => ({ shelfOrder: moveShelf(shop.shelfOrder, shelf, step) }),
    database,
  );
}

/** Löschen ist weich; Artikel dieses Ladens stehen danach unter „egal“. */
export async function deleteShop(id: string, database: KraftwerkDatabase = db): Promise<void> {
  const shop = await database.shops.get(id);
  if (!shop || shop.deletedAt) return;
  const now = stamp();
  await writeRow('shops', { ...shop, deletedAt: now, updatedAt: now }, 'delete', database);
}

/** Einkaufstage ab `from`, aufsteigend. */
export async function listTrips(
  householdId: string | null,
  from: string,
  database: KraftwerkDatabase = db,
): Promise<LocalHouseholdEvent[]> {
  if (!householdId) return [];
  return (await database.household_events.where('householdId').equals(householdId).toArray())
    .filter((row) => !row.deletedAt && !row.cancelled && row.shopping && row.date >= from)
    .sort((a, b) => a.date.localeCompare(b.date) || (a.time ?? '').localeCompare(b.time ?? ''));
}

export async function planTrip(
  input: {
    householdId: string;
    date: string;
    time: string | null;
    shopId: string | null;
    title: string;
  },
  database: KraftwerkDatabase = db,
): Promise<LocalHouseholdEvent> {
  return writeRow(
    'household_events',
    {
      id: newId(),
      updatedAt: stamp(),
      deletedAt: null,
      householdId: input.householdId,
      title: input.title,
      date: input.date,
      time: input.time,
      durationMin: input.time ? 60 : null,
      endDate: null,
      location: null,
      note: null,
      activity: null,
      activityMin: null,
      cancelled: false,
      forUserIds: [],
      shopping: true,
      shopId: input.shopId,
    },
    'upsert',
    database,
  );
}

export async function removeTrip(id: string, database: KraftwerkDatabase = db): Promise<void> {
  const row = await database.household_events.get(id);
  if (!row || row.deletedAt) return;
  const now = stamp();
  await writeRow(
    'household_events',
    { ...row, deletedAt: now, updatedAt: now },
    'delete',
    database,
  );
}
